import React from "react";
import { useTranslation } from "react-i18next";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";

import { getPaymentTermLabel } from "../../form-fields/PaymentTerm";
import { FormValues } from "../../schemas";
import { FREQ_LABELS } from "../constants";
import { SummaryRow, SummarySection } from "../SummaryRow";

interface Step7Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  currency: string;
  isSimpleLoan: boolean;
  hasPrincipalSchedule: boolean;
  hasRegularInterest: boolean;
  hasPIK: boolean;
  facilityCreated: boolean;
  serverError: string[] | null;
  editMode?: boolean;
}

export const Step7Review: React.FC<Step7Props> = ({
  formik,
  dateFormat,
  currency,
  isSimpleLoan,
  hasPrincipalSchedule,
  hasRegularInterest,
  hasPIK,
  facilityCreated,
  serverError,
  editMode = false
}) => {
  const { t } = useTranslation("debt");
  const { values } = formik;

  const fmtDate = (d: string | null | undefined) => (d ? dayjs(d).format(dateFormat || "YYYY-MM-DD") : "—");
  const fmtRate = (r: number | null | undefined) => (r != null ? `${r}%` : null);
  const fmtFreq = (f: string | null | undefined) => (f ? (FREQ_LABELS[f] ?? f) : null);
  const fmtFloat = (f: string) => ({ fixed: "Fixed", linked: "Linked", spread: "Spread" })[f] ?? f;

  return (
    <Box>
      <SummarySection title={t("wizard.review.debtStructure")}>
        <SummaryRow
          label={t("wizard.review.type")}
          value={isSimpleLoan ? t("wizard.debtConfig.simpleLoan") : t("wizard.debtConfig.creditFacility")}
        />
        <SummaryRow label={t("wizard.review.paymentTerms")} value={getPaymentTermLabel(values.payment_term, t)} />
        <SummaryRow
          label={isSimpleLoan ? t("wizard.review.loanAmount") : t("wizard.review.commitmentAmount")}
          value={`${currency} ${values.loan_amount?.toLocaleString()}`}
        />
        <SummaryRow label={t("wizard.review.startDate")} value={fmtDate(values.start_date)} />
        <SummaryRow label={t("wizard.review.maturityDate")} value={fmtDate(values.maturity_date)} />
        <SummaryRow label={t("wizard.review.statusReporting")} value={fmtFreq(values.loan_status_frequency)} />
        {isSimpleLoan && values.loan_first_status_date && (
          <SummaryRow label={t("wizard.review.firstStatusDate")} value={fmtDate(values.loan_first_status_date)} />
        )}
      </SummarySection>

      {hasPrincipalSchedule && (
        <SummarySection title={t("wizard.review.principalRepayment")}>
          <SummaryRow label={t("wizard.common.frequency")} value={fmtFreq(values.principal_frequency)} />
          <SummaryRow
            label={t("wizard.common.referenceIndex")}
            value={values.principal_reference_index ?? t("wizard.principalSetup.notLinked")}
          />
          {values.principal_reference_index && (
            <SummaryRow label={t("wizard.common.linkPercentage")} value={fmtRate(values.principal_link_percent)} />
          )}
          {isSimpleLoan && values.principal_first_payment_date && (
            <SummaryRow
              label={t("wizard.common.firstPaymentDate")}
              value={fmtDate(values.principal_first_payment_date)}
            />
          )}
        </SummarySection>
      )}

      {hasRegularInterest && (
        <SummarySection title={t("wizard.review.regularInterest")}>
          <SummaryRow label={t("wizard.common.baseRate")} value={fmtRate(values.base_rate)} />
          <SummaryRow label={t("wizard.common.rateStructure")} value={fmtFloat(values.float_type)} />
          {values.interest_reference_index && (
            <SummaryRow label={t("wizard.common.referenceIndex")} value={values.interest_reference_index} />
          )}
          {values.float_type === "spread" && (
            <SummaryRow label={t("wizard.common.rateFixing")} value={values.interest_rate_fixing} />
          )}
          {values.float_type === "linked" && (
            <SummaryRow label={t("wizard.common.linkPercentage")} value={fmtRate(values.interest_link_percent)} />
          )}
          <SummaryRow label={t("wizard.common.paymentFrequency")} value={fmtFreq(values.interest_frequency)} />
          <SummaryRow label={t("wizard.common.dayCountConvention")} value={values.day_count_convention} />
          {values.floor_rate != null && (
            <SummaryRow label={t("wizard.common.floorRate")} value={fmtRate(values.floor_rate)} />
          )}
          {values.ceiling_rate != null && (
            <SummaryRow label={t("wizard.common.ceilingRate")} value={fmtRate(values.ceiling_rate)} />
          )}
          {isSimpleLoan && values.interest_first_payment_date && (
            <SummaryRow
              label={t("wizard.common.firstPaymentDate")}
              value={fmtDate(values.interest_first_payment_date)}
            />
          )}
          {isSimpleLoan && values.interest_reference_day && (
            <SummaryRow label={t("wizard.common.referenceDate")} value={fmtDate(values.interest_reference_day)} />
          )}
          {values.loan_reset_frequency && (
            <SummaryRow
              label={t("wizard.interestTerms.loanResetFrequencyLabel")}
              value={fmtFreq(values.loan_reset_frequency)}
            />
          )}
          {isSimpleLoan && values.loan_reset_date && (
            <SummaryRow label={t("wizard.interestTerms.loanResetDateLabel")} value={fmtDate(values.loan_reset_date)} />
          )}
          {values.loan_reset_date_offset != null && (
            <SummaryRow label={t("wizard.common.resetDateOffset")} value={values.loan_reset_date_offset} />
          )}
        </SummarySection>
      )}

      {hasPIK && (
        <SummarySection title={t("wizard.review.pikInterest")}>
          <SummaryRow label={t("wizard.common.baseRate")} value={fmtRate(values.pik_base_rate)} />
          <SummaryRow label={t("wizard.common.rateStructure")} value={fmtFloat(values.pik_float_type)} />
          {values.pik_reference_index && (
            <SummaryRow label={t("wizard.common.referenceIndex")} value={values.pik_reference_index} />
          )}
          {isSimpleLoan && values.pik_reference_day && (
            <SummaryRow label={t("wizard.common.referenceDate")} value={fmtDate(values.pik_reference_day)} />
          )}
          <SummaryRow
            label={t("wizard.review.capitalizationFrequency")}
            value={fmtFreq(values.pik_capitalization_frequency)}
          />
          {isSimpleLoan && values.pik_first_capitalization_date && (
            <SummaryRow
              label={t("wizard.pikTerms.firstCapLabel")}
              value={fmtDate(values.pik_first_capitalization_date)}
            />
          )}
          {!hasRegularInterest && (
            <SummaryRow label={t("wizard.common.dayCountConvention")} value={values.day_count_convention} />
          )}
          {!hasRegularInterest && values.loan_reset_frequency && (
            <SummaryRow
              label={t("wizard.interestTerms.loanResetFrequencyLabel")}
              value={fmtFreq(values.loan_reset_frequency)}
            />
          )}
          {!hasRegularInterest && isSimpleLoan && values.loan_reset_date && (
            <SummaryRow label={t("wizard.interestTerms.loanResetDateLabel")} value={fmtDate(values.loan_reset_date)} />
          )}
          {!hasRegularInterest && values.loan_reset_date_offset != null && (
            <SummaryRow label={t("wizard.common.resetDateOffset")} value={values.loan_reset_date_offset} />
          )}
        </SummarySection>
      )}

      {values.fees.length > 0 && (
        <SummarySection title={t("wizard.review.fees")}>
          {values.fees.map((fee, i) => (
            <SummaryRow
              key={i}
              label={fee.type.replace(/_/g, " ")}
              value={
                fee.percentage_of
                  ? `${fee.amount}% of ${fee.percentage_of.replace(/_/g, " ")}`
                  : `${currency} ${fee.amount?.toLocaleString()}`
              }
            />
          ))}
        </SummarySection>
      )}

      {facilityCreated && (
        <Box
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "success.main",
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          <CheckCircleIcon sx={{ color: "success.main" }} />
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            {editMode ? t("wizard.review.changesSaved") : t("wizard.review.facilitySaved")}
          </Typography>
        </Box>
      )}

      {serverError && (
        <Box sx={{ mt: 3, p: 3, borderRadius: 2, border: "1px solid", borderColor: "error.main", bgcolor: "error.50" }}>
          <Typography variant="body2" color="error.main" fontWeight={600} gutterBottom>
            {t("wizard.common.validationError")}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {serverError.map((msg, i) => (
              <Typography key={i} component="li" variant="body2" color="error.dark" sx={{ wordBreak: "break-word" }}>
                {msg}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
